import {PDFCheckBox, PDFField, PDFSignature, PDFTextField} from 'pdf-lib';
import {ValueResolver, ValueType, ValueResolverOptions} from '@/engine/types';
import {Mappings, PdfFieldType} from '@/engine/types/renderer/pdf';
import {logger} from '@/initializers/logger';
import {captureGroupedException} from '@/utils/sentry-helpers';
import {isStampField, UNMAPPED_STAMP_PREFIX} from './stamps';

export const getPdfFieldType = (field: PDFField, resolvedName?: string): PdfFieldType => {
  if (field instanceof PDFTextField) return PdfFieldType.Text;
  if (field instanceof PDFCheckBox) return PdfFieldType.Checkbox;

  if (field instanceof PDFSignature) {
    const fieldName = resolvedName ?? field.getName();

    if (!isStampField(fieldName)) return PdfFieldType.Signature;

    if (fieldName.startsWith(UNMAPPED_STAMP_PREFIX)) return PdfFieldType.Empty;

    return PdfFieldType.Stamp;
  }

  return PdfFieldType.Unknown;
};

export const safeResolver =
  (valueResolver: ValueResolver): ValueResolver =>
  <T = string>(ref?: ValueType | ValueType[], options?: ValueResolverOptions<T>): T => {
    try {
      return valueResolver(ref, options);
    } catch (error) {
      logger.warn('Error resolving ref: ', ref);
      captureGroupedException(error instanceof Error ? error : new Error(String(error)), {
        fingerprint: ['pdf-mapping-resolution-error'],
        extra: {ref},
      });
      return '' as T;
    }
  };

// Take keys containing "!index_name-INDEX" and "index_name-INDEX" and normalize the indexes based on filled in values.
// Because fields like J6 are in the same index regardless of the type of account and are splitted in two list in the PDF.
//
// Given values for:
//
//   J6-0+!index_name-0="Code A"
//   J6-1+index_name-1="Code B"
//   index_name-1=TSX-A
//   guarantee-1=110
//
// Rewrites the mapping to have:
//
//   J6-0+!index_name-0="Code A"
//   J6-0+index_name-0="Code B"
//
// Finally, global field `index_name-INDEX` and `guarantee-INDEX` are rewritten following the moved indexes.
// Since those are only used in a single group `index_name`, we move them only in those cases.
//
// In the above example, the final mapping would be:
//
//   J6-0+!index_name-0="Code A"
//   J6-0+index_name-0="Code B"
//   index_name-0=TSX-A
//   guarantee-0=110
export function normalizeIndexes(input: Mappings): Mappings {
  const output: Mappings = {...input};
  const j6ToReorderRegex = /^J6-(\d+)(@\w+)?\+(!?index_name)-\d+$/;
  const j6RelatedRegex = /^J\d+-(\d+)(@\w+)?\+(.+)?(!?index_name)/;
  const groupedItems: {index: number; groupName: string}[] = [];

  Object.entries(input).forEach(([key, value]) => {
    const [_fullName, originalIndex, _optionalProperty, groupName] = Array.from(key.match(j6ToReorderRegex) ?? []);
    if (originalIndex && value) groupedItems.push({index: parseInt(originalIndex), groupName});
  });

  const groups: Record<string, {index: number; groupName: string}[]> = {};
  groupedItems.forEach(item => {
    groups[item.groupName] = groups[item.groupName] ?? [];
    groups[item.groupName].push(item);
  });

  for (const [groupName, items] of Object.entries(groups)) {
    const newIndexes = items
      .sort((a, b) => a.index - b.index)
      .reduce(
        (acc, item, newIndex) => {
          if (item.index == newIndex) return acc;
          acc.add([item.index, newIndex]);
          return acc;
        },
        new Set() as Set<[number, number]>,
      );

    Object.entries(input).forEach(([key, value]) => {
      const matchIndexReplace = Array.from(newIndexes).find(([oldIndex]) => key.endsWith(`-${oldIndex}`));

      if (key.match(j6RelatedRegex) && key.includes(`+${groupName}`) && matchIndexReplace) {
        const newKey = key.replaceAll(`-${matchIndexReplace[0]}`, `-${matchIndexReplace[1]}`);
        output[key] = '';
        output[newKey] = value;
      }
    });

    if (groupName === 'index_name') {
      for (const [oldIndex, newIndex] of Array.from(newIndexes)) {
        output[`index_name-${newIndex}`] = input[`index_name-${oldIndex}`];
        output[`index_name-${oldIndex}`] = '';

        if (`guarantee-${oldIndex}` in input) {
          output[`guarantee-${newIndex}`] = input[`guarantee-${oldIndex}`];
          output[`guarantee-${oldIndex}`] = '';
        }

        const oldCompositeKey = `guarantee-${oldIndex}+index_name-${oldIndex}`;
        const newCompositeKey = `guarantee-${newIndex}+index_name-${newIndex}`;
        if (oldCompositeKey in input) {
          output[newCompositeKey] = input[oldCompositeKey];
          output[oldCompositeKey] = '';
        }
      }
    }
  }

  return output;
}
